import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  HouseHeart,
  DollarSign,
  Sparkles,
  Wrench,
  Shield,
  Lock,
  BellRing,
  CheckCircle2,
  HandCoins,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
  Cctv
} from
  'lucide-react';

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const advantages = [
    {
      icon: DollarSign,
      title: 'Vacancy is Our Problem, Not Yours',
      description: 'Your rent is guaranteed in the lease and paid on the first of every month. If the unit sits empty, that\'s our exposure, not yours. You collect the same amount regardless of what\'s happening with occupancy.'
    },
    {
      icon: Shield,
      title: '$1M+ Commercial Liability Coverage',
      description: 'Every property we operate is covered under a commercial liability policy through Proper Insurance, not a standard renter\'s policy. You\'re protected at a level most individual landlords never have access to.'
    },
    {
      icon: Wrench,
      title: 'Free Minor Repairs',
      description: 'We handle small repairs and maintenance at our own expense. No calls to you for minor issues. Your property stays in top condition without pulling you in.'
    },
    {
      icon: HandCoins,
      title: 'Fully Hands-Off for You',
      description: 'We manage everything: guests, cleaning, maintenance coordination, and access. Once the lease is signed, your only job is cashing the rent.'
    }
  ];


  const techFeatures = [
    {
      icon: Shield,
      title: 'Guest Screening and Agreements',
      description: 'Every guest is held to a higher standard than a typical rental applicant. Before anyone sets foot in the unit, they go through Truvi\'s full screening process: ID verification, criminal background check, and sex offender registry check. Contact information is collected and a rental agreement is signed for every stay. Occupancy limits are strictly enforced.'
    },
    {
      icon: Cctv,
      title: 'Exterior Entry Monitoring',
      description: 'We monitor the front entry of every property we operate. This creates a documented record of everyone coming and going, helping us enforce occupancy limits and flag unauthorized access before it becomes a problem.'
    },
    {
      icon: BellRing,
      title: 'Noise Monitoring',
      description: 'Minut sensors detect sustained noise levels in real time. No audio recording, full landlord privacy compliance. Problems are flagged and addressed before they escalate into damage or neighbor complaints.'
    }
  ];


  return (
    <div className="landing-page">
      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <a href="#top" className="logo-section" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <HouseHeart className="logo-icon" />
            <span className="logo-text logo-text-nav">ENVOY STAYS</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="main-nav">
            <a href="#advantages">Benefits</a>
            <a href="#technology">Safety & Technology</a>
            <a href="#faq">FAQ</a>
            <a href="#about">About</a>
            <a href="#contact">
              <Button variant="default" size="sm">Partner With Us</Button>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <a href="#advantages" onClick={() => setMobileMenuOpen(false)}>Benefits</a>
            <a href="#technology" onClick={() => setMobileMenuOpen(false)}>Safety & Technology</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
            <a href="#about" onClick={() => setMobileMenuOpen(false)}>About</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="default" size="sm" className="w-full">Partner With Us</Button>
            </a>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-headline">
              Lease Your Property with Confidence.
            </h1>
            <p className="hero-subheadline">
              We partner with property owners through long-term master leases. Guaranteed rent on the first of every month, professional property care, and zero daily involvement on your end.
            </p>
            <div className="hero-cta">
              <Button size="lg" className="inline-flex items-center justify-center whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-primary/90 h-10 cta-primary !font-medium !text-sm gap-2 px-8 rounded-md shadow text-primary-foreground bg-primary" onClick={() => document.getElementById('advantages').scrollIntoView({ behavior: 'smooth' })}>
                See How It Works
              </Button>
              {/* <Button size="lg" variant="outline" className="cta-secondary" onClick={() => document.getElementById('advantages').scrollIntoView({ behavior: 'smooth' })}>
                Learn More
              </Button> */}
            </div>
          </div>
        </div>
      </section>

      {/* The Landlord Advantage */}
      <section id="advantages" className="advantages-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why Property Owners Partner With Us</h2>
            <p className="section-subtitle">Unreliable tenants and unpredictable income are the two biggest problems in residential rentals. We eliminate both with a master lease, guaranteed monthly rent, and a professional team handling everything in between.

            </p>
          </div>
          <div className="advantages-grid">
            {advantages.map((advantage, index) => {
              const IconComponent = advantage.icon;
              return (
                <Card key={index} className="advantage-card">
                  <CardContent className="advantage-content">
                    <div className="advantage-icon">
                      <IconComponent size={32} />
                    </div>
                    <h3 className="advantage-title">{advantage.title}</h3>
                    <p className="advantage-description">{advantage.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Safety & Technology */}
      <section id="technology" className="technology-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Safety & Technology</h2>
            <p className="section-subtitle">Your property is protected before, during, and after every stay through verified screening, controlled access, and real-time monitoring.

            </p>
          </div>
          <div className="tech-grid">
            {techFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="tech-feature">
                  <div className="tech-icon-wrapper">
                    <IconComponent size={40} />
                  </div>
                  <h3 className="tech-title">{feature.title}</h3>
                  <p className="tech-description">{feature.description}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Everything you need to know about partnering with Envoy Stays for corporate housing.
            </p>
          </div>
          <div className="faq-content">
            <Accordion type="single" collapsible className="faq-accordion">

              <AccordionItem value="item-1">
                <AccordionTrigger>How long are your lease agreements?</AccordionTrigger>
                <AccordionContent>
                  Our leases start at 12 months. In practice, we aim to stay much longer. Stability is the foundation of our model. Long-term commitments mean better care of the property, more predictable income for you, and lower turnover costs for us.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger>How is rent guaranteed if there are no guests?</AccordionTrigger>
                <AccordionContent>
                  Our rent obligation to you is contractual, not occupancy-dependent. We sign a master lease and pay you on the 1st of every month regardless of whether the unit is occupied. Vacancy is our business risk, not yours. That's the core of our model.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>Who is responsible for property maintenance?</AccordionTrigger>
                <AccordionContent>
                  We handle all day-to-day maintenance and minor repairs at our expense. For any larger issues, we coordinate with you promptly. Because we're in the property regularly managing guest turnovers, we catch small issues before they become expensive ones. That's something most traditional landlords never get with a long-term tenant.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger>Who are your guests?</AccordionTrigger>
                <AccordionContent>
                  We primarily serve traveling professionals such as travel nurses on hospital assignments, consultants on corporate deployments, and business travelers on extended engagements.
                  Every guest goes through a screening process before they ever set foot in the unit. That includes ID verification, a criminal background check, and a sex offender registry check. These are working professionals on assignment, and we hold them to a higher standard than a typical rental application.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger>Do I have any say in who stays in my property?</AccordionTrigger>
                <AccordionContent>
                  You won't need to be involved in guest selection day-to-day, but you can take comfort in knowing our screening process is rigorous. Every guest goes through identity verification before receiving access. We enforce occupancy limits, maintain a detailed entry log through our smart lock system, and conduct regular property inspections. You hand us the keys once. We handle everything from there with the same care we'd give a property we owned ourselves.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger>What if a guest damages my property?</AccordionTrigger>
                <AccordionContent>
                  Every guest is ID-verified and screened before receiving access to the property. We conduct regular inspections and professional cleanings between every stay, so damage is caught quickly rather than discovered months later. In the rare event of significant damage, our commercial insurance policy provides coverage and we handle the coordination. You are not left navigating a claim on your own.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7">
                <AccordionTrigger>What happens if someone gets hurt on my property during a guest stay?</AccordionTrigger>
                <AccordionContent>
                  This is exactly why we carry a $1M commercial liability policy through Proper Insurance, a carrier purpose-built for corporate housing operators. Unlike a standard renter's policy, our coverage is specifically designed for furnished, short and mid-term rental operations. We're happy to name you as an Additional Interested Party on our Certificate of Insurance so you have direct visibility into our coverage. You are not exposed and unprotected. That's a promise we back with paper.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8">
                <AccordionTrigger>What about my insurance? Does your business model affect my policy?</AccordionTrigger>
                <AccordionContent>
                  We recommend notifying your insurer of the lease arrangement, and we're happy to provide our Certificate of Insurance for your records. Our $1M commercial liability policy through Proper Insurance is primary coverage specifically designed for this use case. Most landlord policies are structured around traditional tenants, so having our commercial policy as the primary layer of protection is actually an upgrade on what you'd have with a standard renter.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9">
                <AccordionTrigger>What happens if short-term rentals become illegal in my city?</AccordionTrigger>
                <AccordionContent>
                  Your rent is guaranteed by contract regardless of what happens with local STR regulations. If regulatory changes affect our operations, we shift to mid-term rentals (30 or more day stays), which are governed by different rules and are far less restricted. Either way, your lease and your income remain intact.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10">
                <AccordionTrigger>How do I get my property back if I need to sell or move back in?</AccordionTrigger>
                <AccordionContent>
                  Our lease includes clear early termination provisions with defined notice periods. We are professional operators and have no interest in holding a property hostage. When it's time to part ways, we coordinate a smooth transition and return your property professionally cleaned and in excellent condition.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-11">
                <AccordionTrigger>What does the end of our lease agreement look like?</AccordionTrigger>
                <AccordionContent>
                  At the end of our agreed lease term, you simply let us know whether you'd like to renew or conclude the partnership. If we're wrapping up, we coordinate a smooth move-out, have the property professionally cleaned, remove all furnishings, return every key and access device, and leave the unit in excellent condition. There is no drama, no dispute, and no pressure to renew if it no longer works for you. Our reputation depends on every landlord relationship ending as well as it began.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-12">
                <AccordionTrigger>How do I know you are a legitimate operation and not just someone subletting without permission?</AccordionTrigger>
                <AccordionContent>
                  Envoy Stays is the corporate housing brand of Carino Investments, LLC, a registered LLC with over four years of operational experience in corporate housing. We carry a commercial liability policy through Proper Insurance, operate under a formal master lease agreement, and run a structured guest screening process that includes ID verification, a criminal background check, and a sex offender registry check. We welcome any due diligence. Business documentation and our Certificate of Insurance are available upon request.
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </div>
        </div>
      </section>

      {/* About Our LLC */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">About Envoy Stays</h2>
              <p className="about-description">
                Envoy Stays is the corporate housing brand of Carino Investments, LLC.
              </p>
              <p className="about-description">
                We lease residential properties directly from owners under long-term master lease agreements, furnish them to a professional standard, and house traveling professionals including nurses, contractors, and corporate executives on extended stays.
              </p>
              <p className="about-description">
                We've operated furnished housing for over four years. This is a structured business with commercial insurance, proper legal agreements, and a clear operational framework designed to protect your property and your income.
              </p>
              <p className="about-description">
                When you partner with us, you get a single professional tenant, our LLC, on a long-term lease. We handle everything from there.
              </p>
              <div className="about-features">
                <div className="about-feature">
                  <CheckCircle2 className="feature-check" size={20} />
                  <span>$1M+ Commercial Liability Insurance through Proper Insurance</span>
                </div>
                <div className="about-feature">
                  <CheckCircle2 className="feature-check" size={20} />
                  <span>Long-Term Master Lease with Carino Investments, LLC as your tenant</span>
                </div>
                <div className="about-feature">
                  <CheckCircle2 className="feature-check" size={20} />
                  <span>Minor repairs covered at our expense</span>
                </div>
              </div>
            </div>
            <div className="about-visual">
              <div className="about-stat-card">
                <div className="stat-number">$1M+</div>
                <div className="stat-label">Liability Coverage</div>
              </div>
              <div className="about-stat-card">
                <div className="stat-number">1st</div>
                <div className="stat-label">Rent Guaranteed</div>
              </div>
              <div className="about-stat-card">
                <div className="stat-number">24/7</div>
                <div className="stat-label">Property Monitoring</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner With Us */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="contact-content">
            <h2 className="section-title">Partner With Us</h2>
            <p className="section-subtitle">
              We partner with property owners across the country. If you have a residential unit and want guaranteed rent without the landlord headaches, let's find out if it's a good fit.
            </p>
            <div className="contact-cta">
              <a href="mailto:partnerships@envoystays.com" style={{ textDecoration: 'none' }}>
                <Button size="lg" className="cta-large">
                  Get in Touch
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="footer-logo">
                <HouseHeart className="logo-icon" />
                <span className="logo-text logo-text-footer">ENVOY STAYS</span>
              </div>
              <p className="footer-tagline">Your Property, Expertly Furnished. Your Rent, Guaranteed.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-heading">Company</h4>
                <a href="#about">About Us</a>
                <a href="#advantages">Benefits</a>
                <a href="#technology">Safety & Technology</a>
              </div>
              <div className="footer-column">
                <h4 className="footer-heading">Contact</h4>
                <a href="mailto:partnerships@envoystays.com">partnerships@envoystays.com</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Envoy Stays. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>);

};

export default LandingPage;